import { CheckInput } from 'components/form/Check';
import { RadioInput } from 'components/form/Radio';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input, Panel, Select, Control } from '../../components';

import { ControlsContainer, RadioWrap } from './BenchmarkFormStyles';
import { useMultipleQueryForm } from './hooks/useMultipleQueryForm';
import { BenchmarkNameMaxLength } from 'modules/Benchmarks/BenchmarkList/utils';

const Custom = 6;

export enum CalculationFrequency {
  day = 1,
  month = 2,
  quarter = 3,
}

export enum NumberOfDaysInYear {
  days_365 = 1,
  days_252 = 2,
}

type Props = {
  title: string;
  boxTitle: string;
  isTypeOptions?: boolean;
  isRebalanceFrequency?: boolean;
  isPerformanceVariations?: boolean;
  isCustomMonths?: boolean;
  isNumberOfDaysInYear?: boolean;
  isCustomMonthsEnd?: boolean;
};

export const BenchmarkForm: React.FC<Props> = ({
  title,
  boxTitle,
  isTypeOptions,
  isRebalanceFrequency,
  isPerformanceVariations,
  isCustomMonths,
  isNumberOfDaysInYear,
  isCustomMonthsEnd,
}) => {
  const { getMultipleQueryForm } = useMultipleQueryForm();

  const [showNumberOfDaysInYear, setShowNumberOfDaysInYear] = useState(false);

  const {
    control,
    register,
    formState: { errors },
    watch,
    unregister,
    setValue,
  } = useFormContext();

  const rebalanceFrequencyWatch = watch('rebalanceFrequency');
  const calculationFrequencyWatch = watch('calculationFrequencyId');

  useEffect(() => {
    setShowNumberOfDaysInYear(
      (isNumberOfDaysInYear ?? false) &&
        calculationFrequencyWatch === CalculationFrequency.day
    );
    if (
      (isNumberOfDaysInYear ?? false) &&
      calculationFrequencyWatch === CalculationFrequency.day
    ) {
      // setValue('numberOfDaysInYearId', NumberOfDaysInYear.days_365);
    } else {
      unregister('numberOfDaysInYearId');
      unregister('calendarType');
    }
  }, [isNumberOfDaysInYear, calculationFrequencyWatch, setValue, unregister]);

  const {
    typeOptions,
    calculationFrequencyOptions,
    baseCurrencyOptions,
    calendarTypeOptions,
    rebalanceFrequencyOptions,
    rebalanceFrequencyCustomMonthsOptions,
    performanceVariationOptions,
    numberOfDaysInYear,
    isLoading: isLoadingBenchmark,
  } = getMultipleQueryForm();

  const renderTypeOptions = () => {
    if (isTypeOptions) {
      return (
        <Control label='Weighting Methodology'>
          <Controller
            control={control}
            name='weightingMethodology'
            render={({ field: { onChange, value } }) => (
              <Select
                placeholder='Select'
                testid='weightingMethodology'
                options={typeOptions || []}
                onChange={(val) => onChange(val.value)}
                value={(typeOptions || []).filter(
                  (option: any) => option.value === value
                )}
                style={{ width: 200 }}
                errorMessage={
                  errors.weightingMethodology &&
                  errors.weightingMethodology.message
                }
              />
            )}
          />
        </Control>
      );
    }
    return null;
  };

  const renderOptions = () => (
    <ControlsContainer>
      {calculationFrequencyWatch === CalculationFrequency.day && (
        <Control label='Calendar type'>
          <Controller
            control={control}
            name='calendarType'
            render={({ field: { onChange, value } }) => (
              <Select
                placeholder='Select'
                testid='calendarType'
                options={calendarTypeOptions || []}
                value={(calendarTypeOptions || []).filter(
                  (option: any) => option.value === value
                )}
                onChange={(val) => onChange(val.value)}
                style={{ width: 175 }}
              />
            )}
          />
        </Control>
      )}
      {isCustomMonthsEnd && (
        <Control label='Use Daily To Monthly Linking'>
          <RadioWrap>
            <RadioInput register={register('customMonthEnd')} value='Yes'>
              Yes
            </RadioInput>
            <RadioInput register={register('customMonthEnd')} value='No'>
              No
            </RadioInput>
          </RadioWrap>
        </Control>
      )}
      {isPerformanceVariations && (
        <Control label='Performance Variations'>
          <Controller
            control={control}
            name='performanceVariationCode'
            render={({ field: { onChange, value } }) => (
              <Select
                placeholder='Select'
                testid='performanceVariationCode'
                options={performanceVariationOptions || []}
                value={(performanceVariationOptions || []).filter(
                  (option: any) => option.value === value
                )}
                onChange={(val) => onChange(val.value)}
                style={{ width: 175 }}
              />
            )}
          />
        </Control>
      )}
      <Control>
        <CheckInput register={register('isIncludeBookedValues')}>
          Include Booked Values
        </CheckInput>
      </Control>
    </ControlsContainer>
  );

  const renderForm = () => (
    <ControlsContainer>
      <Control label='Name'>
        <Input
          {...register('name')}
          placeholder='Enter Name'
          errorMessage={errors.name && errors.name.message}
          style={{ width: 250 }}
          maxLength={BenchmarkNameMaxLength}
          data-testid='benchmark_name'
        />
      </Control>
      <Control label='Calculation Frequency'>
        <Controller
          control={control}
          name='calculationFrequencyId'
          render={({ field: { onChange, value } }) => (
            <Select
              testid='calculationFrequencyId'
              {...register('calculationFrequencyId')}
              placeholder='Select'
              options={calculationFrequencyOptions || []}
              value={(calculationFrequencyOptions || []).filter(
                (option: any) => option.value === value
              )}
              onChange={(val) => {
                onChange(val.value);
              }}
              errorMessage={
                errors.calculationFrequencyId &&
                errors.calculationFrequencyId.message
              }
              style={{ width: 200 }}
            />
          )}
        />
      </Control>
      {isRebalanceFrequency && (
        <Control label='Rebalance Frequency'>
          <Controller
            control={control}
            name='rebalanceFrequency'
            render={({ field: { onChange, value } }) => (
              <Select
                {...register('rebalanceFrequency')}
                testid='rebalanceFrequency'
                placeholder='Select'
                options={rebalanceFrequencyOptions || []}
                value={(rebalanceFrequencyOptions || []).filter(
                  (option: any) => option.value === value
                )}
                onChange={(val) => {
                  onChange(val.value);
                }}
                errorMessage={
                  errors.rebalanceFrequency && errors.rebalanceFrequency.message
                }
                style={{ width: 200 }}
              />
            )}
          />
        </Control>
      )}
      {isRebalanceFrequency &&
        (rebalanceFrequencyWatch || isCustomMonths) === Custom && (
          <Control label='Months'>
            <Controller
              control={control}
              name='rebalanceFrequencyCustomMonths'
              render={({ field: { onChange, value } }) => (
                <Select
                  {...register('rebalanceFrequencyCustomMonths')}
                  testid='rebalanceFrequencyCustomMonths'
                  withCheckboxes
                  placeholder='Select'
                  options={rebalanceFrequencyCustomMonthsOptions || []}
                  value={value}
                  onChange={(val) => onChange(val)}
                  errorMessage={
                    errors.rebalanceFrequencyCustomMonths &&
                    errors.rebalanceFrequencyCustomMonths.message
                  }
                  style={{ width: 200 }}
                />
              )}
            />
          </Control>
        )}
      <Control label='Base Currency'>
        <Controller
          control={control}
          name='baseCurrency'
          render={({ field: { onChange, value } }) => (
            <Select
              placeholder='Select'
              testid='baseCurrency'
              options={baseCurrencyOptions || []}
              value={(baseCurrencyOptions || []).filter(
                (option: any) => option.code === value
              )}
              onChange={(val) => onChange(val.code)}
              errorMessage={errors.baseCurrency && errors.baseCurrency.message}
              style={{ width: 100 }}
            />
          )}
        />
      </Control>
      {showNumberOfDaysInYear && (
        <Control label='Number of Days in Year'>
          <Controller
            control={control}
            name='numberOfDaysInYearId'
            render={({ field: { onChange, value } }) => (
              <Select
                placeholder='Select'
                testid='numberOfDaysInYearId'
                options={numberOfDaysInYear || []}
                value={(numberOfDaysInYear || []).filter(
                  (option: any) => option.value === value
                )}
                onChange={(val) => onChange(val.value)}
                errorMessage={
                  errors.numberOfDaysInYearId &&
                  errors.numberOfDaysInYearId.message
                }
                style={{ width: 100 }}
              />
            )}
          />
        </Control>
      )}

      {renderTypeOptions()}
    </ControlsContainer>
  );

  return (
    <Panel
      title={title}
      boxTitle={boxTitle}
      labelOptions='Advanced Options'
      options={renderOptions()}
      children={renderForm()}
      isLoading={isLoadingBenchmark}
    />
  );
};
